/**
 * Pacotes de sessões (esteticista), plano seção 17.3 e épico 7.
 *
 * O saldo é derivado, não um contador: sessões usadas = sessões já feitas antes
 * do cadastro + atendimentos concluídos que usaram o pacote. Assim, dois aparelhos
 * concluindo atendimentos offline nunca "perdem" uma sessão na sincronização.
 */
import type { Appointment, Package } from '../schemas/entities';
import { type LocalDate, toMs } from './time';

export interface PackageBalance {
  total: number;
  used: number;
  remaining: number;
  expired: boolean;
  active: boolean;
}

export function packageBalance(
  pkg: Package,
  appointments: Pick<Appointment, 'package_id' | 'status' | 'deleted_at'>[],
  today?: LocalDate,
): PackageBalance {
  const usedInApp = appointments.filter(
    (a) => a.package_id === pkg.id && a.status === 'completed' && !a.deleted_at,
  ).length;
  const used = pkg.sessions_used_initial + usedInApp;
  const remaining = Math.max(0, pkg.sessions_total - used);
  const expired = Boolean(today && pkg.expires_at && pkg.expires_at < today);
  return { total: pkg.sessions_total, used, remaining, expired, active: !pkg.deleted_at && remaining > 0 && !expired };
}

/** Pacote ativo do cliente para o serviço (o mais antigo primeiro, para gastar antes). */
export function activePackageFor(
  packages: Package[],
  appointments: Pick<Appointment, 'package_id' | 'status' | 'deleted_at'>[],
  clientId: string,
  serviceId: string,
  today?: LocalDate,
): { pkg: Package; balance: PackageBalance } | null {
  const candidates = packages
    .filter((p) => !p.deleted_at && p.client_id === clientId && p.service_id === serviceId)
    .sort((a, b) => toMs(a.purchased_at) - toMs(b.purchased_at));
  for (const pkg of candidates) {
    const balance = packageBalance(pkg, appointments, today);
    if (balance.active) return { pkg, balance };
  }
  return null;
}

/**
 * Pacote ativo para qualquer um dos serviços do atendimento.
 *
 * Um atendimento pode ter vários serviços; o pacote é sempre de um só. Se a
 * cliente tiver pacote de mais de um deles, gasta o mais antigo -- é a regra
 * que já vale entre pacotes do mesmo serviço, e a que menos surpreende: o que
 * foi comprado primeiro vence primeiro.
 *
 * Com um serviço só -- o caso de hoje -- é idêntico a activePackageFor.
 */
export function activePackageForAny(
  packages: Package[],
  appointments: Pick<Appointment, 'package_id' | 'status' | 'deleted_at'>[],
  clientId: string,
  serviceIds: string[],
  today?: LocalDate,
): { pkg: Package; balance: PackageBalance } | null {
  const candidates = packages
    .filter((p) => !p.deleted_at && p.client_id === clientId && serviceIds.includes(p.service_id))
    .sort((a, b) => toMs(a.purchased_at) - toMs(b.purchased_at));
  for (const pkg of candidates) {
    const balance = packageBalance(pkg, appointments, today);
    if (balance.active) return { pkg, balance };
  }
  return null;
}

/** Sessão que este atendimento vai usar: "sessão 4 de 10" (com 7 de 10 ainda no saldo). */
export function nextSessionLabel(balance: { remaining: number; total: number }): string {
  const n = Math.min(balance.total, Math.max(1, balance.total - balance.remaining + 1));
  return `sessão ${n} de ${balance.total}`;
}
